import React, { useRef, type PropsWithChildren, memo } from 'react';
import { type RefetchFn, graphql, useFragment } from 'react-relay';
import type { GraphQLTaggedNode } from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import { useQueryParam } from '@atlassian/react-resource-router';

import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import type { FilterRefetchQuery } from '../../__generated__/FilterRefetchQuery.graphql';
import { ActiveJqlProvider } from '../../services/active-jql/index.tsx';
import { FilterQueryProvider } from '../../services/filter-query/index.tsx';
import type { IssueSearchInputProvider_jiraFilter$key } from './__generated__/IssueSearchInputProvider_jiraFilter.graphql';
import type { IssueSearchInputProvider_jiraView$key } from './__generated__/IssueSearchInputProvider_jiraView.graphql';

type Props = PropsWithChildren<{
	// Remove on cleanup of jira_nin_last_used_jql
	defaultJql: string;
	filterQuery: GraphQLTaggedNode;
	jiraFilter: IssueSearchInputProvider_jiraFilter$key | null;
	jiraView: IssueSearchInputProvider_jiraView$key | null;
	onFilterKeyToJql?: (filter: string) => string | undefined;
	refetch: RefetchFn<FilterRefetchQuery>;
}>;

export const IssueSearchInputProvider = memo(
	({
		children,
		defaultJql,
		filterQuery,
		refetch,
		onFilterKeyToJql,
		jiraFilter,
		jiraView,
	}: Props) => {
		const viewMetadata = useFragment<IssueSearchInputProvider_jiraView$key>(
			graphql`
				fragment IssueSearchInputProvider_jiraView on JiraIssueSearchViewMetadata
				@argumentDefinitions(
					lastUsedJql: {
						type: "Boolean!"
						provider: "@atlassian/jira-relay-provider/src/jira-nin-last-used-jql.relayprovider"
					}
				) {
					jql @include(if: $lastUsedJql)
				}
			`,
			jiraView,
		);

		const filterData = useFragment<IssueSearchInputProvider_jiraFilter$key>(
			graphql`
				fragment IssueSearchInputProvider_jiraFilter on JiraFilter {
					jql
					... on JiraCustomFilter {
						isEditable
					}
				}
			`,
			jiraFilter,
		);

		const [jqlParam, setJqlParam] = useQueryParam('jql');
		const [filterParam] = useQueryParam('filter');

		// JQL is first read from the query param
		let jql = jqlParam;

		let filterId;
		let filterJql;

		if (isFilterId(filterParam)) {
			filterId = filterParam;
			filterJql = filterData?.jql;
		} else {
			// If filter param is present (but is not an ID) try map to a hardcoded JQL value
			if (filterParam !== undefined) {
				filterJql = onFilterKeyToJql?.(filterParam);

				// If no explicit JQL query param is defined then use the mapped filter value.
				if (jql === undefined) {
					jql = filterJql;
				}
			}

			// Otherwise, if there is not filter query param, use the resolved JQL from the issue search response
			if (jql === undefined) {
				jql = fg('jira_nin_last_used_jql') ? (viewMetadata?.jql ?? undefined) : defaultJql;
			}
		}

		// Ensure the JQL param reflects the resolved JQL value
		if (jql !== undefined && jql !== jqlParam) {
			setJqlParam(jql, 'replace');
		}

		const isFilterEditable = filterData?.isEditable;

		const initialJql = useRef(
			fg('jracloud_85520_hydration_ref_only_on_page_load') ? (jql ?? filterJql) : undefined,
		);

		return (
			<FilterQueryProvider query={filterQuery} refetch={refetch} filterId={filterId}>
				<ActiveJqlProvider
					initialJql={initialJql.current}
					filterId={filterId}
					filterJql={filterJql}
					jql={jql ?? undefined}
					isFilterEditable={isFilterEditable ?? undefined}
				>
					{children}
				</ActiveJqlProvider>
			</FilterQueryProvider>
		);
	},
);
